import React from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Grid,
    InputLabel,
    OutlinedInput,
    IconButton,
    Typography,
    Chip,
    FormControlLabel,
    Checkbox,
    Select,
    MenuItem
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { IAddUpdateMenuItemRequest } from "../../../../model/admin-portal/request/add-update-menu-request";
import { ICustomPageListResponse } from "../../../../model/admin-portal/response/custom-page-list-response";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface MenuItemFormProps {
    id: string;
    item: IAddUpdateMenuItemRequest;
    itemIndex: number;
    onUpdate: (itemIndex: number, field: string, value: any) => void;
    onRemove: (itemIndex: number) => void;
    customPagesList: ICustomPageListResponse[];
    disabled: boolean;
    expanded: boolean;
    onExpandChange: (itemIndex: number, expanded: boolean) => void;
}

export const MenuItemForm: React.FC<MenuItemFormProps> = ({
    id,
    item,
    itemIndex,
    onUpdate,
    onRemove,
    customPagesList,
    disabled,
    expanded,
    onExpandChange
}) => {
    const isDefault = (item as any).isDefault || false;

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id, disabled });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        marginBottom: '16px'
    };

    return (
        <Accordion
            ref={setNodeRef}
            style={style}
            sx={{ mb: 2 }}
            expanded={disabled ? false : expanded}
            onChange={disabled ? undefined : (_, isExpanded) => onExpandChange(itemIndex, isExpanded)}
            disabled={disabled}
        >
            <AccordionSummary
                expandIcon={disabled ? null : <ExpandMoreIcon />}
                sx={{ cursor: disabled ? 'default' : 'pointer' }}
            >
                <Box display="flex" alignItems="center" gap={1} width="100%">
                    <Box
                        {...(!disabled ? attributes : {})}
                        {...(!disabled ? listeners : {})}
                        sx={{ cursor: disabled ? 'default' : 'grab', display: 'flex' }}
                    >
                        <DragIndicatorIcon color={disabled ? "disabled" : "action"} />
                    </Box>
                    <Typography sx={{ flexGrow: 1 }}>
                        {item.title || `Item ${itemIndex + 1}`}
                    </Typography>
                    {isDefault && <Chip label="Padrão" size="small" color="primary" />}
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove(itemIndex);
                        }}
                        disabled={disabled}
                        size="small"
                        color="error"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            </AccordionSummary>

            <AccordionDetails>
                <Grid container spacing={2}>
                    {/* Título e Visível */}
                    <Grid item xs={12} md={10}>
                        <InputLabel>Título *</InputLabel>
                        <OutlinedInput
                            size="small"
                            value={item.title || ''}
                            onChange={(e) => onUpdate(itemIndex, 'title', e.target.value)}
                            fullWidth
                            required
                            error={!item.title || item.title.length === 0}
                            placeholder="Digite o título do item..."
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid item xs={12} md={2} display="flex" alignItems="flex-end">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={item.visible || false}
                                    onChange={(e) => onUpdate(itemIndex, 'visible', e.target.checked)}
                                    disabled={disabled}
                                />
                            }
                            label="Visível"
                        />
                    </Grid>

                    {/* Custom Page Selector */}
                    <Grid item xs={12}>
                        <InputLabel>Página Customizada</InputLabel>
                        <Select
                            size="small"
                            value={item.customPageId || ''}
                            onChange={(e) => onUpdate(itemIndex, 'customPageId', e.target.value || null)}
                            disabled={disabled}
                            fullWidth
                        >
                            <MenuItem value="">Nenhuma (usar URL externa)</MenuItem>
                            {customPagesList.map(page => (
                                <MenuItem key={page.id} value={page.id}>
                                    {page.title}
                                </MenuItem>
                            ))}
                        </Select>
                        <Typography variant="caption" color="textSecondary">
                            Selecione uma página customizada ou deixe em branco para usar URL externa
                        </Typography>
                    </Grid>

                    {/* URL (se não tem customPageId) - aceita tanto link externo quanto caminho interno do site */}
                    {!item.customPageId && (
                        <Grid item xs={12}>
                            <InputLabel>Link *</InputLabel>
                            <OutlinedInput
                                size="small"
                                value={item.externalUrl || ''}
                                onChange={(e) => onUpdate(itemIndex, 'externalUrl', e.target.value)}
                                disabled={disabled}
                                placeholder="https://... ou /licitacoes"
                                fullWidth
                                error={!item.externalUrl || item.externalUrl.length === 0}
                            />
                            <Typography variant="caption" color="textSecondary">
                                Use um link completo (https://...) para abrir um site externo em nova aba, ou um caminho interno (ex.: /licitacoes, /contratos, /obras, /convenios, /emendas-parlamentares, /prestacao-contas, /documentos-setoriais, /repasses-setoriais, /conselhos, /estoques-medicamento, /filas-espera) para abrir uma página do próprio portal — nesse caso não é preciso trocar nada ao mudar de ambiente (teste/dev/produção).
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};
